import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Lock, Key } from "lucide-react";
import { useTranslation } from "react-i18next";

export const ResetPasswordForm = () => {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showResetForm, setShowResetForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState("");
    const [isValid, setIsValid] = useState(false);
    const { t } = useTranslation();

    // Password validation criteria
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    const hasMinLength = password.length >= 6;

    // Check if password meets all criteria
    useEffect(() => {
        setIsValid(hasLowercase && hasUppercase && hasDigit && hasMinLength);
    }, [hasLowercase, hasUppercase, hasDigit, hasMinLength]);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const toggleResetForm = () => {
        setShowResetForm(prev => !prev);
        // Clear password when hiding the form
        if (showResetForm) {
            setPassword("");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user) {
            toast.error("You must be logged in to update your password");
            return;
        }

        if (!isValid) {
            toast.error("Password doesn't meet the required criteria");
            return;
        }

        setIsLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({
                password
            });

            if (error) throw error;

            toast.success(t("settings.userSettings.password.successMessage"));
            // Reset form state
            setPassword("");
            setShowResetForm(false);
        } catch (error) {
            console.error("Error updating password:", error);
            toast.error(t("settings.userSettings.password.errorMessage"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <Button
                type="button"
                variant={showResetForm ? "secondary" : "outline"}
                onClick={toggleResetForm}
                className="flex items-center gap-2"
            >
                {showResetForm ? <Lock className="h-4 w-4" /> : <Key className="h-4 w-4" />}
                {showResetForm ? t("settings.userSettings.password.cancel") : t("settings.userSettings.password.reset")}
            </Button>

            {showResetForm && (
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                        <Label htmlFor="password">{t("settings.userSettings.password.new")}</Label>
                        <div className="relative">
                            <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder={t("settings.userSettings.password.enterNew")}
                                required
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                                onClick={togglePasswordVisibility}
                            >
                                {showPassword ?
                                    <EyeOff className="h-4 w-4" /> :
                                    <Eye className="h-4 w-4" />
                                }
                            </Button>
                        </div>

                        <div className="text-sm mt-2">
                            <p className="mb-1">{t("settings.userSettings.password.requirements")}</p>
                            <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                                <li className={hasLowercase ? "text-green-500" : ""}>
                                    {t("settings.userSettings.password.lowercase")}
                                </li>
                                <li className={hasUppercase ? "text-green-500" : ""}>
                                    {t("settings.userSettings.password.uppercase")}
                                </li>
                                <li className={hasDigit ? "text-green-500" : ""}>
                                    {t("settings.userSettings.password.number")}
                                </li>
                                <li className={hasMinLength ? "text-green-500" : ""}>
                                    {t("settings.userSettings.password.minLength")}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={isLoading || !isValid}
                        >
                            {isLoading ? t("settings.userSettings.password.updating") : t("settings.userSettings.password.update")}
                            <Save className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};